import { facilitiesApi } from './facilitiesApi';
import { clampPayloadToCurrentScope } from './viewerAccess';

function trimOrNull(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text ? text : null;
}

function toNumberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(String(value).replace(/,/g, ''));
  return Number.isNaN(parsed) ? null : parsed;
}

function toText(value) {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

function toFacilityMetadata(source = {}, row = {}) {
  const metadata = {
    ...((source.capacity_metadata && typeof source.capacity_metadata === 'object' && !Array.isArray(source.capacity_metadata))
      ? source.capacity_metadata
      : {}),
  };

  const feedType = toText(
    row.feedType ??
    row.feed_type ??
    metadata.feed_type ??
    metadata.feedType ??
    source.feedType ??
    source.feed_type ??
    source.facilityType ??
    source.facility_type ??
    ''
  );
  const volumeText = toText(row.volume ?? metadata.volume ?? metadata.annual_production_capacity ?? '');
  const unit = toText(row.unit ?? metadata.unit ?? '');
  const capacityUtil = toText(row.capacityUtil ?? metadata.capacity_utilisation ?? metadata.capacityUtil ?? '');
  const targetSpecies = toText(row.targetSpecies ?? metadata.target_species ?? metadata.targetSpecies ?? '');
  const qualityGrade = toText(row.qualityGrade ?? metadata.quality_grade ?? metadata.qualityGrade ?? '');
  const pricePerUnit = toText(row.pricePerUnit ?? metadata.price_per_unit ?? metadata.pricePerUnit ?? '');

  if (feedType) metadata.feed_type = feedType;
  if (volumeText) {
    metadata.volume = volumeText;
    const numericVolume = toNumberOrNull(volumeText);
    if (numericVolume !== null) metadata.annual_production_capacity = numericVolume;
  }
  if (unit) metadata.unit = unit;
  if (capacityUtil) metadata.capacity_utilisation = capacityUtil;
  if (targetSpecies) metadata.target_species = targetSpecies;
  if (qualityGrade) metadata.quality_grade = qualityGrade;
  if (pricePerUnit) metadata.price_per_unit = pricePerUnit;

  return metadata;
}

function normalizeFacilityType(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_\s-]+/g, '');
}

export function isFeedProductionFacility(source = {}) {
  const rawFacilityType = normalizeFacilityType(
    source.facility_type ??
    source.facilityType ??
    source.raw?.facility_type ??
    source.raw?.facilityType ??
    ''
  );
  const metadataFeedType = normalizeFacilityType(
    source.capacity_metadata?.feed_type ??
    source.capacity_metadata?.feedType ??
    source.raw?.capacity_metadata?.feed_type ??
    source.raw?.capacity_metadata?.feedType ??
    ''
  );

  return rawFacilityType === 'feedmill' || metadataFeedType === 'feedmill';
}

export function normalizeFeedProductionRow(source = {}) {
  const raw = source.raw ?? source;
  const metadata = raw?.capacity_metadata ?? source?.capacity_metadata ?? {};
  const facilityType = toText(
    raw?.facilityType ??
    raw?.facility_type ??
    source?.facilityType ??
    source?.facility_type ??
    metadata.feed_type ??
    metadata.feedType ??
    ''
  );

  const feedType = toText(
    source.feedType ??
    source.feed_type ??
    metadata.feed_type ??
    metadata.feedType ??
    (facilityType ? facilityType : 'Feed Mill')
  );

  const volumeValue =
    source.volume ??
    metadata.volume ??
    metadata.annual_production_capacity ??
    '';

  return {
    id: raw?.uuid ?? raw?.id ?? source.id ?? `local-${Date.now()}`,
    raw,
    feedType,
    facilityName: toText(source.facilityName ?? raw?.name ?? source.name ?? ''),
    name: toText(source.facilityName ?? raw?.name ?? source.name ?? ''),
    volume: toText(volumeValue),
    unit: toText(source.unit ?? metadata.unit ?? (metadata.annual_production_capacity != null ? 'Tonnes' : '')),
    capacityUtil: toText(source.capacityUtil ?? metadata.capacity_utilisation ?? metadata.capacityUtil ?? ''),
    targetSpecies: toText(source.targetSpecies ?? metadata.target_species ?? metadata.targetSpecies ?? ''),
    qualityGrade: toText(source.qualityGrade ?? metadata.quality_grade ?? metadata.qualityGrade ?? ''),
    pricePerUnit: toText(source.pricePerUnit ?? metadata.price_per_unit ?? metadata.pricePerUnit ?? ''),
    state: toText(source.state ?? raw?.state ?? ''),
    lga: toText(source.lga ?? raw?.lga ?? ''),
    ward: toText(source.ward ?? raw?.ward ?? ''),
    gps_coordinates: toText(source.gps_coordinates ?? raw?.gps_coordinates ?? ''),
    dataStatus: toText(source.dataStatus ?? raw?.data_status ?? raw?.validationStatus ?? ''),
    validationStatus: toText(source.validationStatus ?? raw?.validationStatus ?? raw?.data_status ?? ''),
    facilityType,
  };
}

export function buildFeedProductionPayload(row = {}, user = {}, existing = {}) {
  const source = existing?.raw ?? existing ?? {};
  const scopedRow = clampPayloadToCurrentScope(row, user?.roleId);
  const payload = {
    name: trimOrNull(scopedRow.facilityName ?? scopedRow.name ?? source.name),
    facility_type: 'feedmill',
    functional_status: trimOrNull(source.functional_status ?? source.functionalStatus ?? 'fully_functional'),
    ownership: trimOrNull(source.ownership_raw ?? source.ownership ?? 'private'),
    licensed_status: trimOrNull(source.licensed_status ?? source.licensedStatus ?? 'pending'),
    upgrade_priority: trimOrNull(source.upgrade_priority ?? source.upgradePriority ?? 'low'),
    state: trimOrNull(scopedRow.state ?? source.state ?? user?.assignedState ?? null),
    lga: trimOrNull(scopedRow.lga ?? source.lga ?? user?.assignedLga ?? null),
    ward: trimOrNull(scopedRow.ward ?? source.ward ?? user?.assignedWard ?? null),
    gps_coordinates: trimOrNull(scopedRow.gps_coordinates ?? source.gps_coordinates ?? null),
    data_status: trimOrNull(source.data_status ?? source.dataStatus ?? 'draft'),
    capacity_metadata: toFacilityMetadata(source, row),
  };

  return Object.fromEntries(Object.entries(payload).filter(([, value]) => value !== null && value !== undefined));
}

export async function listFeedProductionRows(params = {}) {
  const result = await facilitiesApi.listAll({
    per_page: params.per_page ?? 500,
    facility_type: 'feedmill',
    ...params,
  });

  const items = Array.isArray(result?.items) ? result.items : [];
  return {
    ...result,
    items: items.filter(isFeedProductionFacility).map((item) => normalizeFeedProductionRow(item)),
  };
}

export const feedProductionApi = {
  collectionKey: facilitiesApi.collectionKey,
  listAll: listFeedProductionRows,
  create: async (payload, user = {}) => {
    const created = await facilitiesApi.create(buildFeedProductionPayload(payload, user));
    return normalizeFeedProductionRow(created);
  },
  update: async (uuid, payload, user = {}, existing = {}) => {
    const updated = await facilitiesApi.update(uuid, buildFeedProductionPayload(payload, user, existing));
    return normalizeFeedProductionRow(updated);
  },
  remove: async (uuid) => facilitiesApi.remove(uuid),
};
